
import { mdiFeather } from '@mdi/js'
import Button from '@mui/material/Button'
import MonthPicker from 'components/CalendarView/components/MonthPicker'
import WeekNav from 'components/CalendarView/components/WeekNav'
import EmptyView from 'components/EmptyView'
import { FetchPostDestinations } from 'components/Fetch'
import { FetchBuckets } from 'components/Fetch/FetchBuckets'
import PPSelect, { PPSelectOptions } from 'components/PPSelect'
import SimpleLoader from 'components/SimpleLoader'
import { PlannedPost } from 'interfaces'
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { NavLink, useLocation, useNavigate } from 'react-router'
import CopyPostsPopup from 'routes/publishing/components/CopyPostsPopup'
import { of as observableOf, Subject } from 'rxjs'
import { catchError, exhaustMap, filter, map, mergeMap, switchMap, tap } from 'rxjs/operators'
import { mergeState, resetComposer } from 'services/compose'
import { getDerivedStateFromPost } from 'services/compose/update/actions'
import { connectedDestinationsSelector } from 'services/destinations'
import { initPlans } from 'services/plan/actions'
import { planToProfilesSelector } from 'services/plan/selectors'
import { setCalendarViewMode, setSelectedProfiles } from 'services/post'
import {
  bulkDeletePosts,
  fetchPlannedPostsForRange,
  fetchPostedPostsForRange,
  reorderPosts,
  updatePostRecycle
} from 'services/post/actions'
import {
  calendarMonthStartSelector,
  calendarPostedVisibleSelector,
  calendarWeekStartSelector,
  emptySlotsVisibleSelector,
  newPostSelector,
  postsUISelector,
  selectedProfilesFetchIdsSelector,
  selectedProfilesPPIdsSelector,
  virtualPostsVisibleSelector
} from 'services/post/selectors'
import { AutoPostSlot } from 'services/post/state'
import { checkFeatureAvailability } from 'services/product'
import { message } from 'services/snackbar'
import { FEATURE_POST_COPY, FEATURE_POST_RECYCLE } from 'shared/constants'
import { StoreThunkDispatch } from 'store/state'
import { DAYS_IN_WEEK, getMonthViewDays, getMonthViewDaysByWeeks, getWeekViewDays, WEEKDAY_LABELS } from 'utils/calendar'
import { setPosts, updatePostLocation, updateRecyclePost } from './actions'
import styles from './CalendarRoute.pcss'
import CalendarViewOptions from './components/CalendarViewOptions'
import PostPreviewWithInfo from './components/PostPreviewWithInfo'
import { PostsCalendarView, WeekSelection } from './components/PostsCalendarView/PostsCalendarView'
import { PostsAction, postsReducer } from './reducer'
import { getMovedPostResult, getReorderedPosts } from './utils/reorder'

export function CalendarRoute() {
  const intl = useIntl()
  const dispatch = useDispatch<StoreThunkDispatch>()
  const location = useLocation()
  const navigate = useNavigate()
  const profiles = useSelector(connectedDestinationsSelector)
  const selectedProfilesFetchIds = useSelector(selectedProfilesFetchIdsSelector)
  const newPost = useSelector(newPostSelector)
  const virtualPostsVisible = useSelector(virtualPostsVisibleSelector)
  const [posts, postsDispatch] = useReducer<PlannedPost[], [PostsAction]>(postsReducer, [])
  const postsRef = useRef<PlannedPost[]>(posts)
  const [autoPostSlots, setAutoPostSlots] = useState<AutoPostSlot[]>([])
  const autoPostSlotsRef = useRef<AutoPostSlot[]>(autoPostSlots)
  const [loadingOverlayActive, setLoadingOverlayActive] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const [postInPreview, setPostInPreview] = useState<PlannedPost | null>(null)
  const [withPreview, setWithPreview] = useState(false)
  const [postsToCopy, setPostsToCopy] = useState<PlannedPost[]>([])
  const emptySlotsVisible = useSelector(emptySlotsVisibleSelector)
  const ppids = useSelector(selectedProfilesPPIdsSelector).join(',')
  const mode = useSelector(postsUISelector).calendarMode
  const showPosted = useSelector(calendarPostedVisibleSelector)
  const weekStartDay = useSelector(calendarWeekStartSelector)
  const monthStart = useSelector(calendarMonthStartSelector)
  const plansInfo = useSelector(planToProfilesSelector)
  const now = new Date()
  const startMonthIndex = useRef(now.getMonth())
  const startYear = useRef(now.getFullYear())
  const [selectedYear, setSelectedYear] = useState(now.getFullYear())
  const [selectedMonthIndex, setSelectedMonth] = useState(now.getMonth())
  const [weekSelection, setWeekSelection] = useState<WeekSelection>({ date: now, offset: 0 })
  const [loading, setLoading] = useState(false)
  const [hasPendingPosts, setHasPendingPosts] = useState(false)
  const fetch$ = useRef<Subject<{
    ppids: string[],
    startDate: string,
    endDate: string,
    withEmptySlots: boolean,
    withVirtualPosts: boolean
  }>>(undefined)
  const [totalPostsCount, setTotalPostsCount] = useState<number | null>(null)
  const [showEmptyView, setShowEmptyView] = useState(true)

  const planId = useMemo(() => {
    const ids = ppids.split(',').filter(Boolean)
    if (ids.length === 1) {
      const id = Object.keys(plansInfo).find(k => plansInfo[k].ppids.includes(ids[0]))
      return id
    }
    return null
  }, [ppids, plansInfo])

  const goToPlan = () => {
    navigate(`/plans/${planId}`)
  }

  const visiblePosts = useMemo(() => {
    let visible = emptySlotsVisible ? posts : posts.filter(p => !p.isEmpty)
    if (!showPosted) {
      visible = visible.filter(p => !p.isPosted)
    }
    return visible
  }, [posts, emptySlotsVisible, showPosted])

  const visiblePlannedPosts = useMemo(() => {
    return visiblePosts.filter(p => !p.isEmpty && !p.isPosted)
  }, [visiblePosts])

  useEffect(() => {
    dispatch(initPlans())
  }, [dispatch])

  useEffect(() => {
    // EXPL: keep latest posts/slots in refs so reorder callbacks stay referentially
    // stable across posts updates, enabling the memoized PostsCalendarView to skip re-renders
    postsRef.current = posts
  }, [posts])

  useEffect(() => {
    autoPostSlotsRef.current = autoPostSlots
  }, [autoPostSlots])

  useEffect(() => {
    fetch$.current = new Subject()
    const sub = fetch$.current
      .pipe(
        filter(({ ppids }) => ppids.length > 0),
        tap(() => {
          setLoading(true)
        })
      ).pipe(
        switchMap(({ ppids, startDate, endDate, withEmptySlots, withVirtualPosts }) => {
          return dispatch(fetchPlannedPostsForRange(ppids, startDate, endDate, withEmptySlots, withVirtualPosts))
            .pipe(
              switchMap((plannedResponse: any) => {
                setTotalPostsCount(plannedResponse.totalPostsCount)
                setHasPendingPosts(plannedResponse.hasPendingPosts)
                setAutoPostSlots(plannedResponse.slots)

                setLoading(false)
                setLoadingOverlayActive(false)

                // NOTE: Set planned posts immediately
                postsDispatch(setPosts(plannedResponse.posts))

                // Then fetch posted posts
                return dispatch(fetchPostedPostsForRange(ppids, startDate, endDate))
                  .pipe(
                    map((postedResponse: any) => ({
                      planned: plannedResponse,
                      posted: postedResponse
                    }))
                  )
              }),
              catchError(() => {
                dispatch(message('Error fetching posts! Please refresh.', 'error'))
                setLoading(false)
                setLoadingOverlayActive(false)
                return observableOf({ error: true })
              })
            )
        }))
      .subscribe((response: any) => {
        setShowEmptyView(true)
        if (response.error) {
          return
        }
        // Combine both posted and planned posts, with posted posts first
        const postsArray = [
          ...response.posted.posts.map((p: any) => ({ ...p, isPosted: true })),
          ...response.planned.posts
        ]
        postsDispatch(setPosts(postsArray))
      })

    return () => {
      fetch$.current?.unsubscribe()
      sub.unsubscribe()
    }
  }, [dispatch])

  const weekDays = useMemo(() => {
    const startDayIndex = WEEKDAY_LABELS.indexOf(weekStartDay)
    return getWeekViewDays(new Date(), weekSelection.offset, startDayIndex)
  }, [weekSelection.offset, weekStartDay])

  const monthViewStartsFromCurrentWeek = useMemo(() => {
    return monthStart === 'this-week' && selectedMonthIndex === startMonthIndex.current && selectedYear === startYear.current
  }, [monthStart, selectedMonthIndex, selectedYear])

  const monthVisibleDays = useMemo(() => {
    if (mode !== 'month') {
      return []
    }
    const startDayIndex = WEEKDAY_LABELS.indexOf(weekStartDay)
    if (monthViewStartsFromCurrentWeek) {
      return getMonthViewDaysByWeeks(new Date(), startDayIndex)
    }
    return getMonthViewDays(selectedYear, selectedMonthIndex, WEEKDAY_LABELS.indexOf(weekStartDay))
  }, [monthViewStartsFromCurrentWeek, selectedMonthIndex, selectedYear, weekStartDay, mode])

  const monthPickerRange = useMemo(() => {
    if (!monthViewStartsFromCurrentWeek || monthVisibleDays.length === 0 || monthStart === '1st') {
      return undefined
    }
    return [monthVisibleDays[0].date.getMonth(), monthVisibleDays[monthVisibleDays.length - 1].date.getMonth()] as [number, number]
  }, [monthStart, monthVisibleDays, monthViewStartsFromCurrentWeek])

  useEffect(() => {
    if (monthVisibleDays.length > 0) {
      const startDate = monthVisibleDays[0].date.toISOString()
      const endDate = monthVisibleDays[monthVisibleDays.length - 1]?.date
      if (endDate) {
        endDate.setHours(23, 59, 59) // eslint-disable-line no-magic-numbers
      }
      const ppidsArray = ppids.split(',').filter(Boolean)
      if (ppidsArray.length > 0) {
        fetch$.current?.next({
          ppids: ppidsArray,
          startDate,
          endDate: endDate?.toISOString() + '',
          withEmptySlots: emptySlotsVisible,
          withVirtualPosts: virtualPostsVisible
        })
      }
    }
  }, [
    selectedYear,
    selectedMonthIndex,
    ppids,
    refreshKey,
    emptySlotsVisible,
    monthVisibleDays,
    virtualPostsVisible
  ])

  useEffect(() => {
    if (mode === 'week') {
      const endDate = new Date(weekDays[weekDays.length - 1].date.getTime())
      endDate.setHours(23, 59, 59) // eslint-disable-line no-magic-numbers
      const ppidsArray = ppids.split(',').filter(Boolean)
      if (ppidsArray.length > 0) {
        fetch$.current?.next({
          ppids: ppidsArray,
          startDate: weekDays[0].date.toISOString(),
          endDate: endDate.toISOString(),
          withEmptySlots: emptySlotsVisible,
          withVirtualPosts: virtualPostsVisible
        })
      }
    }
  }, [ppids, refreshKey, mode, weekDays, emptySlotsVisible, virtualPostsVisible])

  const onSelectedMonthChange = (year: number, month: number) => {
    setSelectedYear(year)
    setSelectedMonth(month)
  }

  const selectNextWeek = useCallback(() => {
    setWeekSelection(current => {
      const nextDate = new Date(current.date.getTime())
      nextDate.setDate(nextDate.getDate() + DAYS_IN_WEEK)
      return {
        date: nextDate,
        offset: current.offset + 1
      }
    })
  }, [])

  const selectPrevWeek = useCallback(() => {
    setWeekSelection(current => {
      const nextDate = new Date(current.date.getTime())
      nextDate.setDate(nextDate.getDate() - DAYS_IN_WEEK)
      return {
        date: nextDate,
        offset: current.offset - 1
      }
    })
  }, [])

  const goToToday = () => {
    setSelectedYear(now.getFullYear())
    setSelectedMonth(now.getMonth())
    setWeekSelection({ date: now, offset: 0 })
  }

  const delete$ = useRef<Subject<{
    postIds: string[], ppids: string[], all: boolean, scheduled: boolean, bucketId?: string
  }>>(undefined)
  const reorder$ = useRef<Subject<string[]>>(undefined)
  const recycle$ = useRef<Subject<{ postId: string, value: boolean | Date | number, refresh: boolean }>>(undefined)

  const onEdit = (post: PlannedPost) => {
    dispatch(resetComposer())
    dispatch(mergeState(getDerivedStateFromPost(post, 'planned', post.autoPost ? 'queued' : 'scheduled')))
    navigate('/composer', { state: { floating: true, modal: 'compose', opener: location.pathname, background: location } })
  }

  const nonEmptyPosts = useMemo(() => {
    return posts.filter(p => !p.isEmpty)
  }, [posts])

  // Check for newly created/updated posts while posts page is active. Reload posts to fetch the new records
  useEffect(() => {
    if (newPost || location.state?.refresh) {
      setRefreshKey(current => current + 1)
    }
  }, [newPost, location.state?.refresh])

  // Clear posts and preview when profile selection changes
  useEffect(() => {
    postsDispatch(setPosts([]))
    setAutoPostSlots([])
    setPostInPreview(null)
  }, [selectedProfilesFetchIds])

  const refreshPosts = useCallback(() => {
    setRefreshKey(current => current + 1)
  }, [])

  useEffect(() => {
    delete$.current = new Subject()
    delete$.current
      .pipe(tap(() => {
        setLoadingOverlayActive(true)
      }))
      .pipe(mergeMap(({ ppids, postIds, all, scheduled, bucketId }) => {
        return dispatch(bulkDeletePosts(
          postIds, ppids, scheduled ? 'scheduled' : 'queued', all, bucketId, undefined, undefined, undefined, refreshPosts
        ))
          .pipe(catchError((error: Error) => {
            dispatch(message(intl.formatMessage({ id: 'post.notifications.delete-posts-error' }, { error: error.message }), 'error'))
            return observableOf({ error })
          }))
      }))
      .subscribe((response: any) => {
        if (!response.error) {
          setRefreshKey(current => current + 1)
          setPostInPreview(null)
        }
        setLoadingOverlayActive(false)
      })

    return () => delete$.current?.unsubscribe()
  }, [dispatch, refreshPosts, intl])

  useEffect(() => {
    reorder$.current = new Subject()
    reorder$.current.pipe(
      exhaustMap((ids) => dispatch(reorderPosts(ids)).pipe(catchError(() => {
        dispatch(message(intl.formatMessage({ id: 'post.notifications.update-failed' }), 'error'))
        return observableOf({ error: true })
      })))
    )
      .subscribe(({ error }) => {
        if (error) {
          setRefreshKey(current => current + 1)
        }
        setLoadingOverlayActive(false)
      })

    return () => reorder$.current?.unsubscribe()
  }, [dispatch, intl])

  useEffect(() => {
    recycle$.current = new Subject()
    recycle$.current
      .pipe(mergeMap(({ postId, value, refresh }) => dispatch(updatePostRecycle(postId, value))
        .pipe(
          catchError((e) => {
            const msg = e.response?.error.message || intl.formatMessage({ id: 'errors.generic' })
            dispatch(message(msg, 'error'))
            return observableOf({ error: true })
          }),
          map((response: any) => ({ ...response, refresh }))
        )
      )
      )
      .subscribe(response => {
        if (!response.error) {
          postsDispatch(updateRecyclePost(response.postId, response.value))
        }
        if (response.refresh) {
          setRefreshKey(current => current + 1)
        }
      })

    return () => recycle$.current?.unsubscribe()
  }, [dispatch, intl])

  const previewSelection = useMemo(() => {
    const selectedPostIndex = postInPreview
      ? nonEmptyPosts.findIndex((p: any) => p.id === postInPreview.id)
      : -1
    let hasNext = false
    let hasPrev = false

    if (selectedPostIndex !== -1) {
      hasNext = selectedPostIndex < nonEmptyPosts.length - 1
      hasPrev = selectedPostIndex >= 1
    }
    return {
      selectedPostIndex,
      hasNext,
      hasPrev
    }
  }, [postInPreview, nonEmptyPosts])

  useEffect(() => {
    if (withPreview) {
      const firstNonEmptyPost = posts.find(p => !p.isEmpty)
      setPostInPreview(current => {
        if (!current && firstNonEmptyPost) {
          return firstNonEmptyPost
        }
        if (!firstNonEmptyPost) {
          return null
        }
        return posts.find(p => p.id === current?.id) || null
      })
    } else if (!withPreview) {
      setPostInPreview(null)
    }
  }, [withPreview, posts])

  const onDeletePost = useCallback((id: string, ppid: string, scheduled: boolean, bucketId?: string) => {
    delete$.current?.next({
      postIds: [id],
      ppids: [ppid],
      all: false,
      scheduled,
      bucketId
    })
  }, [])

  const movePost = useCallback((post: PlannedPost, position: 'top' | 'bottom') => {
    const reordered = getMovedPostResult(post, position, postsRef.current, autoPostSlotsRef.current)
    if (reordered) {
      const { ids, posts: reorderedPosts } = reordered
      postsDispatch(setPosts(reorderedPosts))
      reorder$.current?.next(ids)
    }
  }, [])

  const onRecycleChange = useCallback((id: string, value: number | boolean | Date, forceRefresh?: boolean) => {
    const recycleAvailable = dispatch(checkFeatureAvailability(FEATURE_POST_RECYCLE))
    if (!recycleAvailable) {
      return
    }
    const shouldRefresh = forceRefresh || virtualPostsVisible
    recycle$.current?.next({ postId: id, value, refresh: shouldRefresh })
  }, [dispatch, virtualPostsVisible])

  const onCopyPost = useCallback((post: PlannedPost) => {
    const copyAllowed = dispatch(checkFeatureAvailability(FEATURE_POST_COPY))
    if (copyAllowed) {
      setPostsToCopy([post])
    }
  }, [dispatch])

  const closeCopyPostsPopup = useCallback(() => {
    setPostsToCopy([])
  }, [])

  const onPostClick = useCallback((post: PlannedPost) => {
    if (post.isPosted) {
      dispatch(setSelectedProfiles({ profiles: [profiles[post.profileId]], activeView: 'history' }))
      navigate('/history/posted')
      return
    }
    setWithPreview(true)
    setPostInPreview(post)
  }, [dispatch, navigate, profiles])

  const onTogglePreview = (value: 'on' | 'off') => {
    setWithPreview(value === 'on')
  }

  const selectNextPostForPreview = useCallback(() => {
    if (previewSelection.hasNext) {
      const next = nonEmptyPosts[previewSelection.selectedPostIndex + 1]
      setPostInPreview(next)
    }
  }, [previewSelection, nonEmptyPosts])

  const selectPrevPostForPreview = useCallback(() => {
    if (previewSelection.hasPrev) {
      const prev = nonEmptyPosts[previewSelection.selectedPostIndex - 1]
      setPostInPreview(prev)
    }
  }, [previewSelection, nonEmptyPosts])

  const deletePost = useCallback((post: PlannedPost) => {
    onDeletePost(post.id, post.ppPageId, !post.autoPost, post.bucketId)
  }, [onDeletePost])

  const closePreview = useCallback(() => {
    setWithPreview(false)
  }, [])

  const onPostUpdated = (post: PlannedPost) => {
    postsDispatch(updatePostLocation(post.id, post.details?.location))
  }

  const hideEmptyView = () => {
    setShowEmptyView(false)
  }

  const onReorderPosts = useCallback((movedPost: PlannedPost, targetPost: PlannedPost) => {
    const reordered = getReorderedPosts(movedPost, targetPost, postsRef.current, autoPostSlotsRef.current)
    if (reordered) {
      const { posts: reorderedPosts, ids } = reordered
      postsDispatch(setPosts(reorderedPosts))
      reorder$.current?.next(ids)
    }
  }, [])

  const viewModeOptions: PPSelectOptions = useMemo(() => ({
    month: { label: <FormattedMessage id="post.calendar.labels.month" /> },
    week: { label: <FormattedMessage id="post.calendar.labels.week" /> }
  }), [])

  const switchMode = (mode: 'month' | 'week') => {
    dispatch(setCalendarViewMode(mode))
  }

  const showPendingLink = !loading && hasPendingPosts

  return (
    <div className={styles.page}>
      <FetchPostDestinations force={false} />
      <FetchBuckets />
      <div className={styles['header-calendar']}>
        <CalendarViewOptions
          postsCount={totalPostsCount}
          withPreview={withPreview}
          showPendingLink={showPendingLink}
          onTogglePreview={onTogglePreview}
        />
        <div className={`${styles['header-bottom']} ${postInPreview ? styles['w-preview'] : ''}`}>
          <Button onClick={goToToday}>
            <FormattedMessage id="post.calendar.labels.today" />
          </Button>
          <div className={styles.middle}>
            {mode === 'month' ? (
              <MonthPicker
                startMonthIndex={startMonthIndex.current}
                selectedMonthIndex={selectedMonthIndex}
                selectedYear={selectedYear}
                labelRange={monthPickerRange}
                onSelectionChange={onSelectedMonthChange}
              />
            ) : (
              <WeekNav
                days={weekDays}
                withBackNav={weekSelection.offset > 0}
                onNext={selectNextWeek}
                onPrev={selectPrevWeek}
              />
            )}
          </div>
          <div className={styles['options-r']}>
            <PPSelect
              name="View"
              options={viewModeOptions}
              selectedValue={mode}
              className={styles.select}
              id="calendar-view-select"
              onSelectionChange={switchMode}
            />
            {planId && (
              <Button onClick={goToPlan}>
                <FormattedMessage id="post.calendar.labels.edit-plan" />
              </Button>
            )}
          </div>
        </div>
      </div>
      <div className={styles.wrapper}>
        <div className={styles.content}>
          {loadingOverlayActive && (
            <div className={styles['loading-overlay']}>
              <SimpleLoader small />
            </div>
          )}
          <div>
            <PostsCalendarView
              year={selectedYear}
              mode={mode}
              monthIndex={selectedMonthIndex}
              posts={visiblePosts}
              loading={loading}
              activePostId={postInPreview?.id}
              selection={weekSelection}
              className={styles['calendar-box']}
              dimPosted
              onPostClick={onPostClick}
              onPostOrderChanged={onReorderPosts}
            />
          </div>
        </div>
        <div className={`${styles['preview-box']} ${styles.calendar} ${withPreview ? styles.visible : ''}`}>
          {postInPreview && (
            <PostPreviewWithInfo
              post={postInPreview}
              hasNextPost={previewSelection.hasNext}
              hasPreviousPost={previewSelection.hasPrev}
              className={styles.preview}
              onClose={closePreview}
              onSelectNext={selectNextPostForPreview}
              onSelectPrevious={selectPrevPostForPreview}
              skipDeleteConfirm
              onDelete={deletePost}
              onMove={movePost}
              onEdit={onEdit}
              onCopy={onCopyPost}
              onRecycleChange={onRecycleChange}
              onPostUpdated={onPostUpdated}
            />
          )}
        </div>
      </div>
      <CopyPostsPopup
        open={postsToCopy.length > 0}
        posts={postsToCopy}
        allSelected={false}
        postType="planned"
        onClose={closeCopyPostsPopup}
        onCopyStarted={closeCopyPostsPopup}
        onCopyFinished={refreshPosts}
      />
      {!loading && !loadingOverlayActive && visiblePlannedPosts.length === 0 && showEmptyView && (
        <EmptyView
          icon={mdiFeather}
          title="No planned posts for this period"
          subtitle={(
            <div>
              <NavLink
                to={{ pathname: '/composer' }}
                state={{ floating: true, modal: 'compose', opener: location.pathname, background: location }}
              >
                Create a new post
              </NavLink>
              &nbsp;or <NavLink to="/content/search/content">search for content</NavLink>
            </div>
          )}
          top="400px"
          backdrop
          onClose={hideEmptyView}
        />
      )}
    </div>
  )
}

export default CalendarRoute
